// Compliance API — typed fetch wrappers and React Query hooks for the
// compliance obligation/task/filing domain.
//
// Auth: MSAL silent token acquisition using apiScopes from authConfig.
// All requests go through fetchApi() which attaches the Bearer token.

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { requireApiAccessToken } from './authToken';
import { stateTaxClientKeys } from './stateTaxClientsApi';
import type { ComplianceObligation, ComplianceTask, ComplianceDashboardStats, ApiEnvelope, TaskAmountExtract, FilingExtractionResult, IngestSourceResponse, ClientTaxRateConfig, ComplianceNotification, SourceStatement, SalesSourceData, ConversionAccuracySummary, NameMismatchAck, CountyShare, CountyAllocationView, CountyAllocationSaveResult } from '../types';
export { displayAmounts, filedAmounts } from './displayAmounts';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const TIMEOUT_MS = 30_000;

// ---------- Core fetch helper ----------

/**
 * Error thrown for non-2xx API responses. Carries the HTTP status plus the server's
 * machine-readable `code` (when the endpoint sends one) so callers can branch without
 * parsing the human message — e.g. ingest 409 is `cap_exceeded` OR `finalized`. The
 * message alone is the server's fallback string, not i18n-able.
 */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireApiAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    headers['Authorization'] = `Bearer ${token}`;

    if (options?.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: 'Unknown error' })) as { error?: string; code?: string };
      throw new ApiError(errorBody.error ?? `API Error: ${response.status}`, response.status, errorBody.code);
    }

    // 204 No Content (e.g. id-based source-statement delete) — nothing to parse.
    if (response.status === 204) return undefined as T;

    return response.json() as Promise<T>;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ---------- Query key factory ----------

export const obligationKeys = {
  all: ['obligations'] as const,
  active: () => [...obligationKeys.all, 'active'] as const,
  byClient: (clientId: string) => [...obligationKeys.all, 'client', clientId] as const,
};

// ---------- Fetch functions ----------

export async function fetchObligationsByClient(
  clientId: string,
): Promise<ComplianceObligation[]> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceObligation[]>>(
    `/compliance/clients/${encodeURIComponent(clientId)}/obligations`,
  );
  return envelope.data ?? [];
}

export async function fetchActiveObligations(): Promise<ComplianceObligation[]> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceObligation[]>>(
    '/compliance/obligations/active',
  );
  return envelope.data ?? [];
}

export interface CreateObligationPayload {
  state: string;
  obligationType: string;
  obligationDisplayName: string;
  frequency: string;
  dueDayOffset: number;
  leadTimeDays: number;
  formNumber?: string;
  accountNumber?: string;
  startDate?: string;
  informationSource?: string;
  accessOfInformation?: string;
  clientHandlesPayment?: boolean;
}

export async function createObligation(
  clientId: string,
  payload: CreateObligationPayload,
): Promise<ComplianceObligation> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceObligation>>(
    `/compliance/clients/${encodeURIComponent(clientId)}/obligations`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error('No data returned from create obligation');
  return envelope.data;
}

export interface UpdateObligationPayload extends Partial<CreateObligationPayload> {
  isActive?: boolean;
  endDate?: string;
}

/**
 * STX-AUDIT-01: the save succeeded but its "Form changed" history entry could not be written. Never silent (Christian, 2026-11-02):
 * the page shows it with a retry (`retryObligationFormHistory`).
 */
export interface HistoryNotRecorded {
  eventType: 'form_changed';
  before: string | null;
  after: string | null;
}

export type UpdateObligationResult = ComplianceObligation & { historyNotRecorded?: HistoryNotRecorded };

export async function updateObligation(
  id: string,
  payload: UpdateObligationPayload,
): Promise<UpdateObligationResult> {
  const envelope = await fetchApi<ApiEnvelope<UpdateObligationResult>>(
    `/compliance/obligations/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error('No data returned from update obligation');
  return envelope.data;
}

/** Retries a form_changed history entry. 409 `code: stale` when the obligation no longer matches; 503 `code: history_not_recorded` when it fails again. */
export async function retryObligationFormHistory(
  id: string,
  clientId: string,
  entry: { before: string | null; after: string | null },
): Promise<void> {
  await fetchApi<ApiEnvelope<{ recorded: boolean }>>(
    `/compliance/obligations/${encodeURIComponent(id)}/history/form-changed?clientId=${encodeURIComponent(clientId)}`,
    { method: 'POST', body: JSON.stringify(entry) },
  );
}

export async function deactivateObligation(
  id: string,
  clientId: string,
): Promise<void> {
  await fetchApi<ApiEnvelope<null>>(
    `/compliance/obligations/${encodeURIComponent(id)}?clientId=${encodeURIComponent(clientId)}`,
    { method: 'DELETE' },
  );
}

// ---------- React Query hooks ----------

export function useObligationsByClient(clientId: string) {
  return useQuery({
    queryKey: obligationKeys.byClient(clientId),
    queryFn: () => fetchObligationsByClient(clientId),
    enabled: !!clientId,
  });
}

export function useActiveObligations() {
  return useQuery({
    queryKey: obligationKeys.active(),
    queryFn: fetchActiveObligations,
  });
}

export function useCreateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: CreateObligationPayload }) =>
      createObligation(clientId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: obligationKeys.byClient(variables.clientId) });
      void qc.invalidateQueries({ queryKey: obligationKeys.active() });
    },
  });
}

export function useUpdateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateObligationPayload }) =>
      updateObligation(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: obligationKeys.all });
    },
  });
}

export function useRetryObligationFormHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clientId, entry }: { id: string; clientId: string; entry: { before: string | null; after: string | null } }) =>
      retryObligationFormHistory(id, clientId, entry),
    onSuccess: () => {
      // The obligation history card reads under the state-tax-clients key space.
      void qc.invalidateQueries({ queryKey: ['state-tax-clients'] });
    },
  });
}

export function useDeactivateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clientId }: { id: string; clientId: string }) =>
      deactivateObligation(id, clientId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: obligationKeys.all });
    },
  });
}

// ---------- Compliance tasks ----------

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: TaskListFilters) => [...taskKeys.all, 'list', filters] as const,
  detail: (id: string, clientId: string) => [...taskKeys.all, 'detail', id, clientId] as const,
};

export interface TaskListFilters {
  status?: string;
  /** Coarse status buckets: 'open' | 'completed' | 'cancelled' (repeatable). Expanded server-side. */
  buckets?: string[];
  dueAfter?: string;  // ISO date (yyyy-MM-dd), inclusive
  dueBefore?: string; // ISO date (yyyy-MM-dd), inclusive
  clientId?: string;
  assignedToId?: string;
}

export async function fetchTasks(filters: TaskListFilters = {}): Promise<ComplianceTask[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  for (const b of filters.buckets ?? []) params.append('bucket', b);
  if (filters.dueAfter) params.set('dueAfter', filters.dueAfter);
  if (filters.dueBefore) params.set('dueBefore', filters.dueBefore);
  if (filters.clientId) params.set('clientId', filters.clientId);
  if (filters.assignedToId) params.set('assignedToId', filters.assignedToId);

  const qs = params.toString();
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask[]>>(
    `/compliance/tasks${qs ? `?${qs}` : ''}`,
  );
  return envelope.data ?? [];
}

export async function fetchTask(id: string, clientId: string): Promise<ComplianceTask> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(id)}?clientId=${encodeURIComponent(clientId)}`,
  );
  if (!envelope.data) throw new Error('Task not found.');
  return envelope.data;
}

export interface MarkFilingFiledPayload {
  clientId: string;
  confirmationNumber: string;
  filedBy: string;
  amountDue?: number;
  bankDebitAmount?: number;
  bankDebitDate?: string;
  bankDebitAccountMasked?: string;
  notes?: string;
  attachmentBlobName?: string;
  filedAt?: string;
  /** Staff override ack when the extract's business-name match failed (Flow A name guard). */
  nameMismatchAck?: NameMismatchAck;
}

export async function markFilingFiled(
  taskId: string,
  obligationId: string,
  payload: MarkFilingFiledPayload,
): Promise<ComplianceTask> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/mark-filed`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error('No data returned from mark-filed.');
  return envelope.data;
}

export function useTasks(filters: TaskListFilters = {}) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => fetchTasks(filters),
    // STX-UX-01: every filter change mints a new query key; without placeholder data the
    // list unmounts into the loading spinner on each change, which also unmounts the
    // column-header filter panels mid-interaction. Keep showing the previous rows while
    // the narrowed set loads (Dataverse-grid behavior).
    placeholderData: keepPreviousData,
  });
}

export function useTask(id: string, clientId: string) {
  return useQuery({
    queryKey: taskKeys.detail(id, clientId),
    queryFn: () => fetchTask(id, clientId),
    enabled: !!id && !!clientId,
  });
}

// ---------- Attachments (Slice 6b) ----------

/**
 * Upload a file as the attachment for a specific filing. Sends the raw bytes as the request
 * body — file name / clientId / uploadedBy travel as query params, Content-Type is the file's
 * MIME type. Returns the updated task (the filing now carries attachment metadata).
 */
export async function uploadFilingAttachment(
  taskId: string,
  obligationId: string,
  params: { clientId: string; uploadedBy?: string; file: File; kind?: string },
): Promise<ComplianceTask> {
  const qs = new URLSearchParams({
    clientId: params.clientId,
    fileName: params.file.name,
  });
  if (params.uploadedBy) qs.set('uploadedBy', params.uploadedBy);
  if (params.kind) qs.set('kind', params.kind);

  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/attachment?${qs.toString()}`,
    {
      method: 'POST',
      body: params.file,
      // Override the default JSON content type — we're sending raw bytes.
      headers: { 'Content-Type': params.file.type || 'application/octet-stream' },
    },
  );
  if (!envelope.data) throw new Error('No data returned from upload.');
  return envelope.data;
}

/**
 * Hard-remove one uploaded attachment (POS source or Reconcile return/payment doc) by its
 * unique blobName. Returns the updated task. 409 when the filing is finalized (reopen first).
 */
export async function removeFilingAttachment(
  taskId: string,
  obligationId: string,
  clientId: string,
  blobName: string,
): Promise<ComplianceTask> {
  const qs = new URLSearchParams({ clientId, blobName });
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/attachment?${qs.toString()}`,
    { method: 'DELETE' },
  );
  if (!envelope.data) throw new Error('No data returned from remove.');
  return envelope.data;
}

/**
 * Remove a POS/source statement by its slot (1..3). Works for legacy sources stored without a
 * blob reference (the blob-name remove can't target those). Clears the derived computed draft.
 */
export async function removeSourceStatement(
  taskId: string,
  obligationId: string,
  clientId: string,
  slot: number,
): Promise<ComplianceTask> {
  const qs = new URLSearchParams({ clientId, slot: String(slot) });
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/source?${qs.toString()}`,
    { method: 'DELETE' },
  );
  if (!envelope.data) throw new Error('No data returned from remove.');
  return envelope.data;
}

/**
 * What became of the computed DR-15 draft after a source statement was removed. The draft is
 * derived from the SUM of every statement on the obligation, so removing one always invalidates
 * it; the server either rebuilds it from the survivors or clears it and says why.
 *
 * Mirrors the backend `ComputedDraftOutcome` enum (serialized by member name).
 */
export type ComputedDraftOutcome =
  | 'Unchanged'                  // there was no computed draft to invalidate
  | 'Recomputed'                 // a fresh draft is on the extract — refetch the figures
  | 'ClearedNoSourcesLeft'       // that was the last statement; nothing to compute from
  | 'ClearedComputeUnsupported'  // this obligation type has no forward-compute engine
  | 'ClearedComputeFailed';      // the engine ran and declined/threw — see computedDraftMessage

export interface RemoveSourceStatementResult {
  computedDraft: ComputedDraftOutcome;
  /**
   * For `ClearedComputeFailed`, the compute engine's OWN hard-stop reason (e.g. a missing county
   * discretionary surtax rate) — the single most actionable string in the flow. Server-generated
   * English, meant to be rendered verbatim inside a localized sentence, never paraphrased.
   */
  computedDraftMessage?: string | null;
  /**
   * BL-114: true when `ClearedComputeFailed` was the engine's rate-config stop. The UI then says it in the reader's language
   * (`ingestSource.needsRateConfig`); `computedDraftMessage` stays the fallback for every other reason. Absent on older APIs.
   */
  computedDraftNeedsRateConfig?: boolean;
}

/**
 * Sources v2: remove one source statement by its stable id (server-generated GUID).
 * Works for any obligation type; legacy statements without ids keep the slot-based
 * removeSourceStatement above. 404 unknown id; 409 finalized filing.
 *
 * Success is either 204 (nothing to report about the computed draft — fetchApi returns
 * undefined for a bodyless response) or 200 with `{ computedDraft, computedDraftMessage }`.
 * Both are normalized to a result object here so callers never have to distinguish them; a 204
 * simply means `Unchanged`. This used to be typed `Promise<void>` and threw the body away,
 * which made every "cleared" outcome look identical to a clean removal — the figures vanished
 * and the UI said "removed".
 */
export async function removeSourceStatementById(
  taskId: string,
  obligationId: string,
  statementId: string,
  clientId: string,
): Promise<RemoveSourceStatementResult> {
  const qs = new URLSearchParams({ clientId });
  const envelope = await fetchApi<ApiEnvelope<RemoveSourceStatementResult> | undefined>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/source-statement/${encodeURIComponent(statementId)}?${qs.toString()}`,
    { method: 'DELETE' },
  );
  // 204 (or a 200 that somehow carries no data) means there was nothing to report.
  return envelope?.data ?? { computedDraft: 'Unchanged' };
}

/**
 * What the server did when asked to compute a filing's MISSING draft from its stored sources.
 * Mirrors the backend `MissingDraftOutcome` enum (serialized by member name).
 */
export type MissingDraftOutcome =
  | 'Computed'            // a draft was computed and saved — refetch the figures
  | 'DraftExists'         // a draft already existed and was deliberately NOT recomputed
  | 'NoSources'           // no source report is stored on this filing
  | 'ComputeUnsupported'  // this form has no forward-compute engine
  | 'Declined'            // the engine refused or failed — see `message`
  // Fix #1b — only with `refresh: true`, and only when a draft already existed:
  | 'Refreshed'           // recomputed at the current rates, changed, saved — see `before`/`after`
  | 'Unchanged'           // recomputed and identical; nothing written
  | 'RefreshDeclined'     // the engine refused or failed; the existing draft was KEPT — see `message`
  | 'RefreshNoSources';   // a draft with no stored source can't be recomputed; kept

export interface MissingDraftResult {
  outcome: MissingDraftOutcome;
  /** For `Declined` / `RefreshDeclined`, the engine's own reason, rendered verbatim inside a localized sentence. */
  message?: string | null;
  /** For `Refreshed`: the CHANGED lines only, old values. A line the new draft lacks is null on `after`. */
  before?: Record<string, number | null> | null;
  /** For `Refreshed`: the same lines, new values. */
  after?: Record<string, number | null> | null;
}

/**
 * Conversion fix #1: compute ONE filing's draft from the source reports already stored on it.
 * The task page calls this after staff save the county rate that an ingest stopped on, so the
 * report they already uploaded is used instead of being uploaded (and appended) a second time.
 * Without `refresh`, missing drafts only — an existing draft comes back `DraftExists`, untouched.
 *
 * `refresh: true` (fix #1b, ruled 2026-10-21) is a person on THIS task asking for its existing draft
 * at the current rates. It never reaches another task. 409 once filed either way.
 */
export async function computeMissingDraft(
  taskId: string,
  obligationId: string,
  clientId: string,
  options?: { refresh?: boolean },
): Promise<MissingDraftResult> {
  const qs = new URLSearchParams({ clientId });
  if (options?.refresh) qs.set('refresh', 'true');
  const envelope = await fetchApi<ApiEnvelope<MissingDraftResult>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/compute-draft?${qs.toString()}`,
    { method: 'POST' },
  );
  if (!envelope.data) throw new Error('No data returned from compute-draft.');
  return envelope.data;
}

/** obligationType → which document kinds this form accepts (drives the upload slots). */
export const OBLIGATION_DOC_KINDS: Record<string, Array<'return' | 'payment-confirmation'>> = {
  sales_tax: ['return', 'payment-confirmation'],
  solid_waste: ['return', 'payment-confirmation'],
  documentary_stamp_tax: ['return', 'payment-confirmation'],
  communication_services_tax: ['return'],
};

export function docKindsFor(obligationType: string): Array<'return' | 'payment-confirmation'> {
  return OBLIGATION_DOC_KINDS[obligationType] ?? ['return'];
}

/**
 * Document kinds that must be present before a filing is "ready to mark filed".
 * A self-pay obligation (client pays the agency directly) never produces a PTC
 * payment confirmation, so only the return is required.
 */
export function requiredDocKindsFor(
  obligationType: string,
  clientHandlesPayment: boolean,
): Array<'return' | 'payment-confirmation'> {
  const kinds = docKindsFor(obligationType);
  return clientHandlesPayment ? kinds.filter((k) => k !== 'payment-confirmation') : kinds;
}

/**
 * Run GPT-4o extraction on the most-recently-uploaded document for a filing, then upsert
 * the reconciliation extract row. Returns the classification + amounts + review flags.
 */
export async function extractFilingDocument(
  taskId: string,
  obligationId: string,
  params: { clientId: string; kind: string; enteredByName?: string; enteredById?: string },
): Promise<FilingExtractionResult> {
  const qs = new URLSearchParams({ clientId: params.clientId, kind: params.kind });
  if (params.enteredByName) qs.set('enteredByName', params.enteredByName);
  if (params.enteredById) qs.set('enteredById', params.enteredById);
  const envelope = await fetchApi<ApiEnvelope<FilingExtractionResult>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/extract-document?${qs.toString()}`,
    { method: 'POST' },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Extraction failed.');
  return envelope.data;
}

/**
 * Fetch an attachment as a Blob. The backend returns raw bytes (not JSON-enveloped) with the
 * original Content-Type + Content-Disposition, so we bypass fetchApi's JSON parsing.
 */
export async function downloadFilingAttachment(
  taskId: string,
  obligationId: string,
  clientId: string,
): Promise<{ blob: Blob; fileName: string }> {
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;

  const url = `${API_BASE}/compliance/tasks/${encodeURIComponent(taskId)}` +
    `/filings/${encodeURIComponent(obligationId)}/attachment` +
    `?clientId=${encodeURIComponent(clientId)}`;

  const response = await fetch(url, { headers });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: 'Download failed' })) as { error?: string };
    throw new Error(errorBody.error ?? `Download failed: ${response.status}`);
  }

  const fileName = parseFileNameFromContentDisposition(
    response.headers.get('Content-Disposition') ?? '',
  ) ?? 'attachment';
  const blob = await response.blob();
  return { blob, fileName };
}

function parseFileNameFromContentDisposition(header: string): string | null {
  // Prefer RFC 5987 `filename*=UTF-8''<pct-encoded>` which the backend emits.
  const starMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (starMatch) {
    try { return decodeURIComponent(starMatch[1]); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

export function useUploadFilingAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, obligationId, clientId, uploadedBy, file }: {
      taskId: string;
      obligationId: string;
      clientId: string;
      uploadedBy?: string;
      file: File;
    }) => uploadFilingAttachment(taskId, obligationId, { clientId, uploadedBy, file }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.setQueryData(taskKeys.detail(updated.id, updated.clientId), updated);
    },
  });
}

export function useMarkFilingFiled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      obligationId,
      payload,
    }: {
      taskId: string;
      obligationId: string;
      payload: MarkFilingFiledPayload;
    }) => markFilingFiled(taskId, obligationId, payload),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: dashboardKeys.all });
      void qc.setQueryData(taskKeys.detail(updated.id, updated.clientId), updated);
    },
  });
}

export interface ReopenFilingPayload {
  clientId: string;
  mode: 'original' | 'amendment';
}

export async function reopenFiling(
  taskId: string,
  obligationId: string,
  payload: ReopenFilingPayload,
): Promise<ComplianceTask> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/reopen`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error('No data returned from reopen.');
  return envelope.data;
}

export function useReopenFiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      obligationId,
      payload,
    }: {
      taskId: string;
      obligationId: string;
      payload: ReopenFilingPayload;
    }) => reopenFiling(taskId, obligationId, payload),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: dashboardKeys.all });
      void qc.setQueryData(taskKeys.detail(updated.id, updated.clientId), updated);
    },
  });
}

// ---------- Cancel / reopen a task (reversible) ----------

export async function cancelTask(taskId: string, clientId: string): Promise<ComplianceTask> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/cancel`,
    { method: 'POST', body: JSON.stringify({ clientId }) },
  );
  if (!envelope.data) throw new Error('No data returned from cancel.');
  return envelope.data;
}

export async function reopenTask(taskId: string, clientId: string): Promise<ComplianceTask> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/reopen`,
    { method: 'POST', body: JSON.stringify({ clientId }) },
  );
  if (!envelope.data) throw new Error('No data returned from reopen.');
  return envelope.data;
}

export function useCancelTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, clientId }: { taskId: string; clientId: string }) =>
      cancelTask(taskId, clientId),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: dashboardKeys.all });
      // Cancelling drops the task off the client's active set → its next-due cell changes.
      void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'next-due'] });
      void qc.setQueryData(taskKeys.detail(updated.id, updated.clientId), updated);
    },
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, clientId }: { taskId: string; clientId: string }) =>
      reopenTask(taskId, clientId),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: dashboardKeys.all });
      // Reopening puts the task back into the client's active set → its next-due cell changes.
      void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'next-due'] });
      void qc.setQueryData(taskKeys.detail(updated.id, updated.clientId), updated);
    },
  });
}

// ---------- Dashboard ----------

export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  conversionAccuracy: () => [...dashboardKeys.all, 'conversion-accuracy'] as const,
};

export async function fetchDashboardStats(): Promise<ComplianceDashboardStats> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceDashboardStats>>(
    '/compliance/dashboard/stats',
  );
  if (!envelope.data) throw new Error('No data returned from dashboard stats.');
  return envelope.data;
}

export function useDashboardStats() {
  return useQuery({
    queryKey: dashboardKeys.stats(),
    queryFn: fetchDashboardStats,
  });
}

export async function getConversionAccuracy(): Promise<ConversionAccuracySummary> {
  const envelope = await fetchApi<ApiEnvelope<ConversionAccuracySummary>>(
    '/compliance/conversion-accuracy',
  );
  if (!envelope.data) throw new Error('No data returned from conversion-accuracy.');
  return envelope.data;
}

export function useConversionAccuracy() {
  return useQuery({
    queryKey: dashboardKeys.conversionAccuracy(),
    queryFn: getConversionAccuracy,
    // Stale after 5 min — accuracy changes slowly (new reconciled pairs trickle in).
    staleTime: 5 * 60 * 1000,
  });
}

/** One reconciled computed-vs-filed pair in the conversion error-review log. */
export interface ConversionReviewItem {
  taskId: string;
  obligationId: string;
  clientId?: string | null;
  clientName?: string | null;
  obligationType?: string | null;
  obligationName?: string | null;
  periodLabel?: string | null;
  computed: Record<string, number | null>;
  filed: Record<string, number | null>;
  variance: Record<string, number | null>;
  totalDueVariance: number;
  reconciledAt: string;
}

export async function getConversionReview(): Promise<ConversionReviewItem[]> {
  const envelope = await fetchApi<ApiEnvelope<{ items: ConversionReviewItem[] }>>(
    '/compliance/conversion-review',
  );
  return envelope.data?.items ?? [];
}

export function useConversionReview() {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'conversion-review'] as const,
    queryFn: getConversionReview,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------- Task amount extracts (bookkeeping bridge) ----------
// Read-side hook + upsert mutation for the per-filing reported-amounts cache.
// The extractor itself lives in a separate app per the project memory; here we
// just consume + display + accept manual entry. Same shape regardless of source.

export const extractKeys = {
  all: ['task-extracts'] as const,
  byTask: (taskId: string) => [...extractKeys.all, taskId] as const,
};

export async function fetchTaskExtracts(taskId: string): Promise<TaskAmountExtract[]> {
  const envelope = await fetchApi<ApiEnvelope<TaskAmountExtract[]>>(
    `/state-tax/tasks/${encodeURIComponent(taskId)}/extracts`,
  );
  return envelope.data ?? [];
}

export function useTaskExtracts(taskId: string) {
  return useQuery({
    queryKey: extractKeys.byTask(taskId),
    queryFn: () => fetchTaskExtracts(taskId),
    enabled: !!taskId,
  });
}

export interface UpsertTaskExtractPayload {
  source?: 'manual' | 'extracted';
  amounts: Record<string, number | null>;
  /** Grading loop: per amount key, where the value came from (see utils/filedOrigin). */
  filedOrigin?: Record<string, string>;
  extractedFrom?: string;
  extractionConfidence?: number;
  enteredByName?: string;
  enteredById?: string;
  confirmationNumber?: string | null;
  amountPaid?: number | null;
  debitDate?: string | null;
  bankAccountMasked?: string | null;
  paymentStatus?: string | null;
  notes?: string | null;
}

export async function upsertTaskExtract(
  taskId: string,
  obligationId: string,
  payload: UpsertTaskExtractPayload,
): Promise<TaskAmountExtract> {
  const envelope = await fetchApi<ApiEnvelope<TaskAmountExtract>>(
    // Staff (JWT-authed) path — NOT the bridge-key-only `/extract` endpoint, which 401s a
    // staff token and silently drops the filed figures. See SaveTaskExtract on the backend.
    `/state-tax/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/save-extract`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Upsert extract failed.');
  return envelope.data;
}

export function useUpsertTaskExtract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      obligationId,
      payload,
    }: {
      taskId: string;
      obligationId: string;
      payload: UpsertTaskExtractPayload;
    }) => upsertTaskExtract(taskId, obligationId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: extractKeys.byTask(variables.taskId) });
    },
  });
}

/**
 * POST the manual-journal export proxy and return the Xero CSV as a Blob. The backend
 * returns raw CSV bytes (not JSON-enveloped) with Content-Disposition, so this bypasses
 * fetchApi's JSON parsing (same pattern as downloadFilingAttachment). Errors throw
 * ApiError carrying the HTTP status + machine code — 412 = missing G/L mapping
 * (message lists the absent account roles), 409 codes = not_filed / no_filed_figures /
 * zero_return, 503 = conversions bridge unconfigured.
 */
export async function exportFilingJournal(
  taskId: string,
  obligationId: string,
  clientId: string,
): Promise<{ blob: Blob; fileName: string }> {
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;

  const url = `${API_BASE}/state-tax/tasks/${encodeURIComponent(taskId)}` +
    `/filings/${encodeURIComponent(obligationId)}/export-journal` +
    `?clientId=${encodeURIComponent(clientId)}`;

  const response = await fetch(url, { method: 'POST', headers });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: 'Export failed' })) as { error?: string; code?: string };
    throw new ApiError(errorBody.error ?? `Export failed: ${response.status}`, response.status, errorBody.code);
  }

  const fileName = parseFileNameFromContentDisposition(
    response.headers.get('Content-Disposition') ?? '',
  ) ?? 'manual-journal.csv';
  const blob = await response.blob();
  return { blob, fileName };
}

/**
 * Per-obligation-type amount keys, mirroring api/Services/FormProfiles.cs. The manual-entry
 * form + reconciliation cells render exactly these keys for the filing's obligation type.
 * Display labels live in i18n under `amounts.<key>`.
 */
export const FORM_AMOUNT_KEYS: Record<string, readonly string[]> = {
  // 🔴 The Florida remittance rule (ruled 2026-09-01): the DR-15 remits
  // MAX(computedTax, taxCollected). `computedTax`, `overCollection`, `underCollection` and
  // `taxDue` are ordered so the comparison reads top-to-bottom — what was due on taxable sales,
  // what was actually collected, the gap in whichever direction it runs, and what gets remitted.
  // Neither direction may be folded into another figure; see ComputeDr15.
  sales_tax: ['grossSales', 'exemptSales', 'taxableSales', 'computedTax', 'taxCollected', 'overCollection', 'underCollection', 'taxDue', 'surtax', 'collectionAllowance', 'penalty', 'interest', 'totalDue'],
  solid_waste: ['dryCleaningTaxDue', 'newTireFeesDue', 'leadAcidBatteryFeesDue', 'rentalCarSurchargeDue', 'totalFeesDue', 'credits', 'penalty', 'interest', 'totalDue'],
  documentary_stamp_tax: ['docStampTaxDue', 'surtax', 'penalty', 'interest', 'totalDue'],
  communication_services_tax: ['stateTaxPortion', 'grossReceipts237', 'localPortion', 'satellitePortion', 'totalCST', 'collectionAllowance', 'netTax', 'penalty', 'interest', 'totalDue'],
};

export const DR15_AMOUNT_KEYS: ReadonlyArray<string> = FORM_AMOUNT_KEYS.sales_tax;

/**
 * Every obligation type the app knows how to file — the DOMAIN, not a sample of the data.
 *
 * 🔴 Exists because a header-filter option list must never be derived from rows the filters
 * have already removed. The Compliance Tasks page used to compute its obligation options from
 * the server-filtered task set, so the default view (assignee = the signed-in user) could
 * produce zero rows and therefore zero options — a filter panel with nothing in it, which
 * only "Clear filters" could revive. A filter sourced from filtered data can only ever
 * subtract; it can never offer a value that would broaden the view.
 *
 * Derived from {@link FORM_AMOUNT_KEYS} rather than written out again, because that map
 * already mirrors `api/Services/FormProfiles.cs` and is the thing kept in step with the
 * backend. A second hand-maintained list would be a second thing to forget.
 *
 * ⚠ Callers should still UNION this with the types present in their rows. If the backend
 * gains a type before this mirror does, the union keeps it visible as soon as a matching row
 * is on screen — so the stale-mirror failure mode degrades to the old behaviour rather than
 * to something worse.
 */
export const OBLIGATION_TYPES: ReadonlyArray<string> = Object.keys(FORM_AMOUNT_KEYS);

export function amountKeysFor(obligationType: string): readonly string[] {
  return FORM_AMOUNT_KEYS[obligationType] ?? DR15_AMOUNT_KEYS;
}

/**
 * Trigger on-demand task generation for a single client scoped to the given asOfDate.
 * `obligationIds`, when non-empty, scopes generation to just those obligations (the
 * Process-as-task dialog's checklist); omitted/empty generates everything eligible.
 * Returns the tasks created-or-merged in this pass.
 */
export async function generateTasksNow(
  clientId: string,
  asOf: string,
  obligationIds?: string[],
): Promise<ComplianceTask[]> {
  const body: { clientId: string; asOfDate: string; obligationIds?: string[] } = { clientId, asOfDate: asOf };
  if (obligationIds && obligationIds.length > 0) {
    body.obligationIds = obligationIds;
  }
  const envelope = await fetchApi<ApiEnvelope<ComplianceTask[]>>(
    '/compliance/tasks/generate-now',
    { method: 'POST', body: JSON.stringify(body) },
  );
  return envelope.data ?? [];
}

/**
 * Summary returned by the admin catch-up generation endpoint (STX-02) — mirrors
 * api/Services CatchupGenerationSummary (camelCase over the wire).
 */
export interface CatchupGenerationSummary {
  clientId: string;
  fromDate: string;
  toDate: string;
  periodsExamined: number;
  tasksCreated: number;
  tasksMerged: number;
  tasksSkippedExisting: number;
}

/**
 * STX-02 — admin catch-up / historical generation for one client. Walks every period
 * from `fromDate` (omitted ⇒ the backend defaults to the earliest obligation
 * effective-start, floored at 2026-01-01) through today via the range generator
 * (dedup + merge — idempotent; a second run is a no-op). Returns the run summary.
 */
export async function generateCatchupTasks(
  clientId: string,
  fromDate?: string,
): Promise<CatchupGenerationSummary> {
  const body: { clientId: string; fromDate?: string } = { clientId };
  if (fromDate) body.fromDate = fromDate;
  const envelope = await fetchApi<ApiEnvelope<CatchupGenerationSummary>>(
    '/compliance/tasks/generate-catchup',
    { method: 'POST', body: JSON.stringify(body) },
  );
  if (!envelope.data) throw new ApiError('Empty catch-up generation response.', 500);
  return envelope.data;
}

/**
 * Result of an obligation seeding run — mirrors api/Services SeedReport (camelCase
 * over the wire). `dryRun` echoes back what the server actually did, so the UI never
 * has to trust its own request to know whether anything was written.
 */
export interface ObligationSeedReport {
  /** Active Dataverse obligation rows looked at. */
  examined: number;
  /** Rows that were written (apply) or would be written (dry run). */
  seeded: number;
  /** Rows Cosmos already had on the `clientId|obligationType` dedup key. */
  skippedExisting: number;
  /** One human-readable line per row that could NOT be mapped. Never summarised away. */
  rejections: string[];
  /** True when nothing was written. */
  dryRun: boolean;
}

/**
 * Admin-only obligation seeding (spec 2026-08-05, Part A). Imports the active Dataverse
 * `cr8f7_statetaxobligation` master into Cosmos `complianceObligations`.
 *
 * `apply` defaults to FALSE — the safe call is the default one. Only an explicit
 * `apply: true` writes; anything else reports the full plan and touches nothing.
 * Seeded obligations land inert (`generationEnabled: false`), so this can never mint a
 * compliance task as a side effect. Re-running is idempotent (dedup on client+type).
 *
 * Throws ApiError 403 when the caller is not on the server-side admin roster.
 */
export async function seedObligations(apply = false): Promise<ObligationSeedReport> {
  const envelope = await fetchApi<ApiEnvelope<ObligationSeedReport>>(
    '/compliance/obligations/seed',
    { method: 'POST', body: JSON.stringify({ apply }) },
  );
  if (!envelope.data) throw new ApiError('Empty obligation seeding response.', 500);
  return envelope.data;
}

// ── Per-client inbound RESYNC ─────────────────────────────────────────────────
// The scheduled inbound sync (CRM completion → statetax filing marked filed) is
// watermark-driven: it only examines CRM tasks modified after its last run, so a completion
// older than the watermark is invisible to it PERMANENTLY. That is why several May/June/July
// 2026 completions had to be backfilled by hand. This is the manual escape hatch — it rescans
// one client's full completed-task history ignoring the watermark, and never moves it.

/** One CRM completion the resync could not apply, and why. */
export interface InboundResyncSkip {
  /** Dataverse activity id of the completed CRM task — lets staff open the offending record. */
  activityId: string;
  taxType: string;
  periodStart: string;
  /** Exclusive end of the period, as the backend models it. */
  periodEndExclusive: string;
  /** Machine-readable: `no-confident-match` | `error` (`not-allowlisted` is reserved). */
  reason: string;
}

/** What a per-client watermark-ignoring replay actually did. */
export interface InboundResyncResult {
  clientId: string;
  /** CRM completions examined — the client's FULL history, not a window. */
  examined: number;
  /** Filings this run newly flipped to filed. */
  filed: number;
  /** Completions whose filing was already filed — a healthy, idempotent no-op. */
  alreadyFiled: number;
  skipped: number;
  skips: InboundResyncSkip[];
}

/**
 * POST /api/state-tax/clients/{id}/resync-inbound-tasks
 *
 * Idempotent and non-notifying by construction: inbound filing is a no-op on an already-filed
 * filing and the client notification is always suppressed, so pressing this repeatedly cannot
 * double-file or email anyone.
 *
 * Throws ApiError 409 with `code`:
 *   - `inbound_sync_disabled`   — inbound task-sync is off fleet-wide.
 *   - `client_not_allowlisted`  — this client is off the INBOUND allowlist (expected today; the
 *                                 allowlist is still pinned to a single pilot client).
 */
export async function resyncInboundTasks(stateTaxClientId: string): Promise<InboundResyncResult> {
  const envelope = await fetchApi<ApiEnvelope<InboundResyncResult>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/resync-inbound-tasks`,
    { method: 'POST' },
  );
  if (!envelope.data) throw new ApiError('Empty inbound resync response.', 500);
  // The server's record defaults Skips to empty, but a leaner serializer dropping the property
  // must not blow up the panel's `.map` — the skip list is the actionable half of the report.
  return { ...envelope.data, skips: envelope.data.skips ?? [] };
}

/** Result of switching obligation generation on or off. */
export interface ObligationGenerationResult {
  /** Obligation documents actually changed — already-matching ones are not churned. */
  changed: number;
  /** The state they were set to. */
  enabled: boolean;
}

/**
 * Admin-only switch that makes seeded obligations live (or inert again).
 *
 * Scoped to one client when `clientId` is supplied; fleet-wide across every active
 * obligation when it is omitted. `enabled: false` is the rollback — it is the same
 * call, and it is why nothing here is one-way.
 *
 * Throws ApiError 403 when the caller is not on the server-side admin roster.
 */
export async function setObligationGeneration(
  enabled: boolean,
  clientId?: string,
): Promise<ObligationGenerationResult> {
  const body: { enabled: boolean; clientId?: string } = { enabled };
  if (clientId) body.clientId = clientId;
  const envelope = await fetchApi<ApiEnvelope<ObligationGenerationResult>>(
    '/compliance/obligations/generation',
    { method: 'POST', body: JSON.stringify(body) },
  );
  if (!envelope.data) throw new ApiError('Empty generation-toggle response.', 500);
  return envelope.data;
}

/**
 * One row of the Process-as-task preview — the obligations/periods that would be
 * covered if generation ran right now for this client + asOf date.
 */
export interface TaskGenerationPreviewItem {
  obligationId: string;
  obligationType: string;
  obligationDisplayName: string;
  periodEnd: string;
  periodLabel: string;
  dueDate: string;
}

/**
 * Preview what task generation would produce for a client at a given asOf date,
 * without creating anything — powers the Process-as-task dialog's checklist.
 */
export async function previewTasks(clientId: string, asOf: string): Promise<TaskGenerationPreviewItem[]> {
  const envelope = await fetchApi<ApiEnvelope<TaskGenerationPreviewItem[]>>(
    `/compliance/tasks-preview?clientId=${encodeURIComponent(clientId)}&asOf=${encodeURIComponent(asOf)}`,
  );
  return envelope.data ?? [];
}

/**
 * Find the existing task for a client whose period contains the given date.
 * Returns the task id, or null if none found.
 */
export async function findTaskForPeriod(clientId: string, periodDate: string): Promise<string | null> {
  const tasks = await fetchTasks({ clientId });
  const target = new Date(periodDate).getTime();
  const match = tasks.find((t) => {
    const start = new Date(t.periodStartDate).getTime();
    const end = new Date(t.periodEndDate).getTime();
    return target >= start && target <= end;
  });
  return match?.id ?? null;
}

// ---------- Source-report types (Task 7 backend contract) ----------
// SourceStatement and SalesSourceData are defined in src/types/index.ts.
export type { SourceStatement, SalesSourceData };

export const sourceKeys = {
  all: ['task-sources'] as const,
  byTask: (taskId: string) => [...sourceKeys.all, taskId] as const,
};

export async function getTaskSources(taskId: string): Promise<SalesSourceData[]> {
  const envelope = await fetchApi<ApiEnvelope<SalesSourceData[]>>(
    `/state-tax/tasks/${encodeURIComponent(taskId)}/sources`,
  );
  return envelope.data ?? [];
}

export function useTaskSources(taskId: string) {
  return useQuery({
    queryKey: sourceKeys.byTask(taskId),
    queryFn: () => getTaskSources(taskId),
    enabled: !!taskId,
  });
}

// ---------- Computed-draft: ingest-source ----------

export async function ingestSource(
  taskId: string,
  obligationId: string,
  params: { clientId: string; file: File; sourceKind?: string; enteredByName?: string; enteredById?: string; slot?: number },
): Promise<IngestSourceResponse> {
  // Single-call ingest: the file rides in the body (raw bytes), like uploadFilingAttachment.
  // The backend stores it under a source/ prefix (never on filing.Attachments) and extracts.
  const qs = new URLSearchParams({ clientId: params.clientId, fileName: params.file.name });
  if (params.sourceKind) qs.set('sourceKind', params.sourceKind);
  if (params.enteredByName) qs.set('enteredByName', params.enteredByName);
  if (params.enteredById) qs.set('enteredById', params.enteredById);
  if (params.slot != null) qs.set('slot', String(params.slot));
  const envelope = await fetchApi<ApiEnvelope<IngestSourceResponse>>(
    `/compliance/tasks/${encodeURIComponent(taskId)}/filings/${encodeURIComponent(obligationId)}/ingest-source?${qs.toString()}`,
    {
      method: 'POST',
      body: params.file,
      headers: { 'Content-Type': params.file.type || 'application/octet-stream' },
    },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Source ingest failed.');
  return envelope.data;
}

export function useIngestSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, obligationId, clientId, file, sourceKind, enteredByName, enteredById, slot }: {
      taskId: string; obligationId: string; clientId: string; file: File;
      sourceKind?: string; enteredByName?: string; enteredById?: string; slot?: number;
    }) => ingestSource(taskId, obligationId, { clientId, file, sourceKind, enteredByName, enteredById, slot }),
    onSuccess: (_data, v) => { void qc.invalidateQueries({ queryKey: extractKeys.byTask(v.taskId) }); },
  });
}

// ---------- Computed-draft: client tax rate config ----------

export const rateConfigKeys = {
  all: ['rate-config'] as const,
  byClient: (clientId: string) => [...rateConfigKeys.all, clientId] as const,
};

export async function fetchClientTaxRateConfig(clientId: string): Promise<ClientTaxRateConfig> {
  const envelope = await fetchApi<ApiEnvelope<ClientTaxRateConfig>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/tax-rate-config`,
  );
  if (!envelope.data) throw new Error('No rate config returned.');
  return envelope.data;
}

export async function upsertClientTaxRateConfig(clientId: string, payload: ClientTaxRateConfig): Promise<ClientTaxRateConfig> {
  const envelope = await fetchApi<ApiEnvelope<ClientTaxRateConfig>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/tax-rate-config`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Save failed.');
  return envelope.data;
}

export function useClientTaxRateConfig(clientId: string) {
  return useQuery({
    queryKey: rateConfigKeys.byClient(clientId),
    queryFn: () => fetchClientTaxRateConfig(clientId),
    enabled: !!clientId,
  });
}

export function useUpsertClientTaxRateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: ClientTaxRateConfig }) =>
      upsertClientTaxRateConfig(clientId, payload),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: rateConfigKeys.byClient(v.clientId) }); },
  });
}

// ---------- Per-obligation county allocation (effective-dated) ----------
//
// Christian's amendment 2026-09-03: multi-county clients are real, so the model is per-county
// from the start. A single-county client is the trivial one-row case — one allocation row, no
// extra ceremony in the UI.

export const countyAllocationKeys = {
  all: ['county-allocation'] as const,
  byObligation: (clientId: string, obligationId: string) =>
    [...countyAllocationKeys.all, clientId, obligationId] as const,
};

export async function fetchObligationCountyAllocation(
  clientId: string, obligationId: string,
): Promise<CountyAllocationView> {
  const envelope = await fetchApi<ApiEnvelope<CountyAllocationView>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}`
    + `/state-tax-obligations/${encodeURIComponent(obligationId)}/county-allocation`,
  );
  if (!envelope.data) throw new Error('No county allocation returned.');
  return envelope.data;
}

export async function upsertObligationCountyAllocation(
  clientId: string, obligationId: string,
  payload: { effectiveFrom: string; counties: CountyShare[]; note?: string | null },
): Promise<CountyAllocationSaveResult> {
  const envelope = await fetchApi<ApiEnvelope<CountyAllocationSaveResult>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}`
    + `/state-tax-obligations/${encodeURIComponent(obligationId)}/county-allocation`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Save failed.');
  return envelope.data;
}

export function useObligationCountyAllocation(clientId: string, obligationId: string) {
  return useQuery({
    queryKey: countyAllocationKeys.byObligation(clientId, obligationId),
    queryFn: () => fetchObligationCountyAllocation(clientId, obligationId),
    enabled: !!clientId && !!obligationId,
  });
}

export function useUpsertObligationCountyAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, obligationId, payload }: {
      clientId: string;
      obligationId: string;
      payload: { effectiveFrom: string; counties: CountyShare[]; note?: string | null };
    }) => upsertObligationCountyAllocation(clientId, obligationId, payload),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: countyAllocationKeys.byObligation(v.clientId, v.obligationId) });
      // The rate a filing computes at just moved for future computes — but NOT for existing
      // drafts, which are flagged rather than recomputed. Invalidating the rate config keeps the
      // obligation view's fallback fresh where the allocation was cleared.
      void qc.invalidateQueries({ queryKey: rateConfigKeys.byClient(v.clientId) });
    },
  });
}

// ---------- Send-update to client ----------

/**
 * What a send-update did, or (on a preview) would do. An empty `records` array cannot tell a
 * real send apart from "every selected filing was suppressed by CRM task-sync", which is how a
 * send silently vanished in production — hence the explicit outcome.
 */
export type SendUpdateOutcome =
  | 'sent'
  | 'partially_sent'
  | 'nothing_sent_all_suppressed'
  | 'nothing_sent';

/** A filing left out of the notification because the CRM already confirmed it to the client. */
export interface SuppressedFilingInfo {
  obligationId: string;
  obligationDisplayName: string;
  /** Machine-readable reason, currently always 'crm_task_sync'. */
  reason: string;
  suppressedAt: string | null;
}

export interface SendUpdatePreview {
  emailTo: string | null;
  smsTo: string | null;
  /** Mailbox the email will actually be sent FROM — the task owner's when it can be
   *  resolved, otherwise the shared configured mailbox. */
  emailFrom: string | null;
  /** Name the email signs off with. Always the display name of `emailFrom`'s owner (or
   *  "State Tax Team" for the shared mailbox) — the two never disagree. */
  senderName: string | null;
  testRedirect: boolean;
  subject: string;
  html: string;
  sms: string;
  includedObligationIds: string[];
  outcome: SendUpdateOutcome;
  suppressedFilings: SuppressedFilingInfo[];
  /** The language the client will be written in (fleet ruling 2026-10-27: the client's CRM language, read from core). */
  language?: ClientLanguage;
  /** Why: `crm`, `crm-blank`, `crm-invalid`, `crm-unavailable` or `not-connected` (core's route not wired yet). */
  languageSource?: string;
  /** The TEXT's language: it goes to a person, so it can differ from the email's (DP-CLIENT-LANG-1). Absent on an older server. */
  smsLanguage?: ClientLanguage;
  /** Why, in the same vocabulary as {@link languageSource}. */
  smsLanguageSource?: string;
}

export type ClientLanguage = 'en' | 'es' | 'pt';

export interface SendUpdateResult {
  task: ComplianceTask;
  records: ComplianceNotification[];
  outcome: SendUpdateOutcome;
  sentObligationIds: string[];
  suppressedFilings: SuppressedFilingInfo[];
}

export async function previewClientUpdate(
  taskId: string,
  clientId: string,
  obligationIds: string[],
): Promise<SendUpdatePreview> {
  const envelope = await fetchApi<ApiEnvelope<SendUpdatePreview>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/tasks/${encodeURIComponent(taskId)}/send-update`,
    { method: 'POST', body: JSON.stringify({ obligationIds, preview: true }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'No preview data returned.');
  return envelope.data;
}

export async function sendClientUpdate(
  taskId: string,
  clientId: string,
  obligationIds: string[],
): Promise<SendUpdateResult> {
  const envelope = await fetchApi<ApiEnvelope<SendUpdateResult>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/tasks/${encodeURIComponent(taskId)}/send-update`,
    { method: 'POST', body: JSON.stringify({ obligationIds, preview: false }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'No data returned from send-update.');
  return envelope.data;
}

export function useSendClientUpdate(taskId: string, clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (obligationIds: string[]) => sendClientUpdate(taskId, clientId, obligationIds),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: taskKeys.detail(taskId, clientId) }); },
  });
}

// --- S1 batch2 ---
// Overloads that accept attachmentKeys for the selectable-attachment feature (item P).
// key format: "{obligationId}:{kind}" (e.g. "obl-abc:return").
// null/omitted = all attachments (back-compat).

export async function previewClientUpdateWithAttachments(
  taskId: string,
  clientId: string,
  obligationIds: string[],
  attachmentKeys?: string[],
): Promise<SendUpdatePreview> {
  const envelope = await fetchApi<ApiEnvelope<SendUpdatePreview>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/tasks/${encodeURIComponent(taskId)}/send-update`,
    { method: 'POST', body: JSON.stringify({ obligationIds, preview: true, attachmentKeys: attachmentKeys ?? null }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'No preview data returned.');
  return envelope.data;
}

export async function sendClientUpdateWithAttachments(
  taskId: string,
  clientId: string,
  obligationIds: string[],
  attachmentKeys?: string[],
): Promise<SendUpdateResult> {
  const envelope = await fetchApi<ApiEnvelope<SendUpdateResult>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/tasks/${encodeURIComponent(taskId)}/send-update`,
    { method: 'POST', body: JSON.stringify({ obligationIds, preview: false, attachmentKeys: attachmentKeys ?? null }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'No data returned from send-update.');
  return envelope.data;
}

export function useSendClientUpdateWithAttachments(taskId: string, clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ obligationIds, attachmentKeys }: { obligationIds: string[]; attachmentKeys?: string[] }) =>
      sendClientUpdateWithAttachments(taskId, clientId, obligationIds, attachmentKeys),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: taskKeys.detail(taskId, clientId) }); },
  });
}
// --- end S1 batch2 ---

// --- S4 batch2 --- User preferences (saved filter state for ComplianceTasksPage) -----------

/** Opaque filter state saved for a user. Shape owned by ComplianceTasksPage. */
export interface TaskFiltersSnapshot {
  buckets?: string[];
  clientId?: string;
  assignedToId?: string;
  dueRange?: string;
  customFrom?: string;
  customTo?: string;
  obligationTypes?: string[];
  /**
   * The lookup columns' C86 selections (2026-11-03), in the fleet's `{op: 'in' | 'notIn', value: 'a,b[,__TL_NO_VALUE__]'}` shape
   * (picklistSelection). When present they win over `clientId` / `assignedToId` / `obligationTypes`, which stay for older views.
   */
  clientFilter?: { op: string; value: string };
  assigneeFilter?: { op: string; value: string };
  obligationFilter?: { op: string; value: string };
}

export interface UserPreferences {
  id: string;
  userId: string;
  taskFilters?: TaskFiltersSnapshot | null;
  /** Accomplishment-moment settings (the off switch + seen-once record). Opaque to the server. */
  moments?: { enabled?: boolean; seen?: unknown[] } | null;
  /** The person's UI language; null = never chosen (the browser's language governs). */
  language?: 'en' | 'es' | null;
  updatedAt?: string;
}

export const prefsKeys = {
  me: ['user-preferences', 'me'] as const,
};

export async function fetchUserPreferences(): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>(
    '/state-tax/users/me/preferences',
  );
  if (!envelope.data) throw new Error('No preferences data returned.');
  return envelope.data;
}

export async function putUserPreferences(taskFilters: TaskFiltersSnapshot): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>(
    '/state-tax/users/me/preferences',
    { method: 'PUT', body: JSON.stringify({ taskFilters }) },
  );
  if (!envelope.data) throw new Error('No preferences data returned.');
  return envelope.data;
}

export function useUserPreferences() {
  return useQuery({
    queryKey: prefsKeys.me,
    queryFn: fetchUserPreferences,
    // Stale after 5 min — preferences rarely change mid-session.
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveUserPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskFilters: TaskFiltersSnapshot) => putUserPreferences(taskFilters),
    onSuccess: (data) => {
      qc.setQueryData(prefsKeys.me, data);
    },
  });
}
/**
 * Saves ONLY the moment settings. The server merges field-wise, so this never touches the Tasks
 * page's saved filters (and saving those never touches this).
 */
export async function putMomentPreferences(moments: { enabled: boolean; seen: string[] }): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>(
    '/state-tax/users/me/preferences',
    { method: 'PUT', body: JSON.stringify({ moments }) },
  );
  if (!envelope.data) throw new Error('No data returned from preferences.');
  return envelope.data;
}

export function useSaveMomentPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putMomentPreferences,
    onSuccess: (data) => {
      qc.setQueryData(prefsKeys.me, data);
    },
  });
}

/**
 * Saves ONLY the person's language (fleet i18n standard §3). Field-wise merge on the server, so the
 * saved filters and moment settings are untouched; anything but "en"/"es" is refused there.
 */
export async function putLanguagePreference(language: 'en' | 'es'): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>(
    '/state-tax/users/me/preferences',
    { method: 'PUT', body: JSON.stringify({ language }) },
  );
  if (!envelope.data) throw new Error('No data returned from preferences.');
  return envelope.data;
}

export function useSaveLanguagePreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putLanguagePreference,
    onSuccess: (data) => {
      qc.setQueryData(prefsKeys.me, data);
    },
  });
}

/** The signed-in person's proven period facts (server: `AccomplishmentFacts`). */
export interface MyPeriodFacts {
  /** yyyy-MM — the month the period ENDS in (Q3 counts toward September). */
  period: string;
  owed: number;
  filed: number;
  complete: boolean;
  /** Stated only for a complete period. */
  allOnTime: boolean | null;
  /** Sum of RECORDED bank debits; null when any filing lacks one — never estimated. */
  remitted: number | null;
}

export interface MyMomentFacts {
  /** false when the server cannot resolve who you are — show nothing. */
  available: boolean;
  period?: MyPeriodFacts;
  overdue?: number;
  /** § 3.4: consecutive complete, all-on-time months (the period being worked counts once complete). */
  onTimeStreak?: number;
  /** § 3.5: first filed periods inside a newly onboarded client's recorded window (server-proven). */
  firstFilings?: FirstFilingFact[];
}

export interface FirstFilingFact {
  /** Stable per client + tax type + state, so the moment is seen once. */
  momentId: string;
  clientName: string;
  periodLabel: string;
}

export const momentKeys = {
  me: ['moments', 'me'] as const,
};

export async function fetchMyMoments(): Promise<MyMomentFacts> {
  const envelope = await fetchApi<ApiEnvelope<MyMomentFacts>>('/state-tax/users/me/moments');
  return envelope.data ?? { available: false };
}

export function useMyMoments() {
  return useQuery({
    queryKey: momentKeys.me,
    queryFn: fetchMyMoments,
    staleTime: 60 * 1000,
  });
}
/** STX-PROD-03: how fresh the data behind the screens is (server: `SyncStatusFunctions`). */
export interface SyncStatus {
  inboundTaskSync: {
    /** false when the inbound CRM task sync is switched off — then there is nothing to show. */
    enabled: boolean;
    /** UTC of the last CLEAN poll; null until one is recorded. */
    lastSucceededAt: string | null;
  };
}

export async function fetchSyncStatus(): Promise<SyncStatus> {
  const envelope = await fetchApi<ApiEnvelope<SyncStatus>>('/state-tax/sync-status');
  if (!envelope.data) throw new Error('No data returned from sync-status.');
  return envelope.data;
}

export function useSyncStatus() {
  return useQuery({
    queryKey: ['sync-status'] as const,
    queryFn: fetchSyncStatus,
    // The loop runs every 15 min; re-check on focus (long-lived tabs) and every 5 min.
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
/** STX-PROD-01: one Monday-start week (Eastern) of the app's own usage (server: `UsageMetrics`). */
export interface UsageWeek {
  /** yyyy-MM-dd, a Monday. */
  weekStart: string;
  filedInApp: number;
  filedViaCrm: number;
  sourcesUploaded: number;
  peopleFiling: number;
}

export async function fetchUsage(): Promise<{ weeks: UsageWeek[] }> {
  const envelope = await fetchApi<ApiEnvelope<{ weeks: UsageWeek[] }>>('/state-tax/usage');
  return envelope.data ?? { weeks: [] };
}

/** Admin-only on the server; pass `enabled: false` for non-admins so nobody collects a 403. */
export function useUsage(enabled: boolean) {
  return useQuery({
    queryKey: ['usage'] as const,
    queryFn: fetchUsage,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
// --- end S4 batch2 ---

// --- Grading loop P3: the automation-readiness board (spec 2026-10-22 §8; admin-only on the server) ---

export interface ReadinessClientRow {
  clientId: string;
  clientName: string | null;
  frequency: string | null;
  gradedFilings: number;
  /** Consecutive FILINGS, newest backwards, at exact match on all six figures. */
  streak: number;
  /** The PROPOSED eligibility N for this frequency (DP-GRADE-RULE, not ruled yet). */
  n: number | null;
  filingsToEligibility: number | null;
  /** First of the month the client would reach N, if every filing from now matched. */
  etaMonth: string | null;
  eligible: boolean;
  lastFiledAt: string | null;
  lastMissPeriod: string | null;
  lastMissClass: string | null;
  lastMissFigures: string[];
}

export interface ReadinessPeriodPoint {
  period: string;
  graded: number;
  exact: number;
  /** Null when nothing in the period was gradeable: no ground truth is never 100%. */
  exactRate: number | null;
  figureMatchRate: Record<string, number | null>;
  topMissClass: string | null;
  /** The ruling that answered the period's top miss class, e.g. "parser-fix #39". */
  answer: string | null;
  reconstructed: boolean;
}

export interface ReadinessBoard {
  generatedAt: string;
  ruleIsProposed: boolean;
  n: Record<string, number>;
  clients: ReadinessClientRow[];
  trendByFrequency: Record<string, ReadinessPeriodPoint[]>;
}

export async function fetchReadinessBoard(): Promise<ReadinessBoard> {
  const envelope = await fetchApi<ApiEnvelope<ReadinessBoard>>('/state-tax/grading/readiness');
  // A missing board is an API fault: surface it (the card's error state), never a plausible-looking empty board.
  if (!envelope.data) throw new Error('readiness board missing from the response');
  return envelope.data;
}

/** One task the nightly window sweep would act on (`WindowSweepItem` on the server). `note` is the server's own wording. */
export interface WindowSweepItem {
  taskId: string;
  clientId: string;
  clientName: string | null;
  periodLabel: string;
  status: string;
  note: string;
}

/** GET state-tax/obligations/window-sweep/preview (admin-only; writes nothing). */
export interface WindowSweepPreview {
  flagEnabled: boolean;
  windowsFrom: string;
  /** False when the Dataverse read failed: both sides were then Cosmos-only, so there is no real delta. */
  deltaValid: boolean;
  newlyCancelled: WindowSweepItem[];
  noLongerCancelled: WindowSweepItem[];
  cosmosOnly: { cancel: WindowSweepItem[]; review: WindowSweepItem[] };
  withDataverse: { cancel: WindowSweepItem[]; review: WindowSweepItem[] };
}

export async function fetchWindowSweepPreview(): Promise<WindowSweepPreview> {
  const envelope = await fetchApi<ApiEnvelope<WindowSweepPreview>>('/state-tax/obligations/window-sweep/preview');
  if (!envelope.data) throw new Error('window-sweep preview missing from the response');
  return envelope.data;
}

/**
 * The sweep's admin dry run. Never fetched on load (`enabled: false`): an admin presses the button, which calls `refetch`.
 * Every press asks the server again (`staleTime: 0`), because the answer is a snapshot of tonight's sweep, not a setting.
 */
export function useWindowSweepPreview() {
  return useQuery({
    queryKey: ['obligations', 'window-sweep', 'preview'] as const,
    queryFn: fetchWindowSweepPreview,
    enabled: false,
    staleTime: 0,
    retry: false,
  });
}

/** Admin-only on the server; pass `enabled: false` for non-admins so nobody collects a 403. */
export function useReadinessBoard(enabled: boolean) {
  return useQuery({
    queryKey: ['grading', 'readiness'] as const,
    queryFn: fetchReadinessBoard,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
